const AbstractPaymentMethodConfig = require('@/lib/PaymentMethods/AbstractPaymentMethodConfig');
const VerifoneActionList = require('./Constants/VerifoneActionList');
const VerifoneIntegrationType = require('./VerifoneIntegrationType');
const VerifoneStatusList = require('./Constants/VerifoneStatusList');
const { toBool, toInt, toString } = require('@/functions/transform');

class VerifoneConfig extends AbstractPaymentMethodConfig {
    constructor() {
        super();

        /**
         * IP address of the Worldpay terminal.
         * For POS Client setups this is the IP of the POS Client software - usually 127.0.0.1
         * For VX820IP setups this is the IP of the terminal itself.
         *
         * @type {string}
         */
        this.terminalHost = '';

        /**
         * Port for the 'integration socket' connection to the terminal.
         *
         * @type {number}
         */
        this.terminalPort = 25000;

        /**
         * Port for the 'progress socket' connection to the terminal (VX820 only).
         * Only used in POS Client mode.
         *
         * @type {number}
         */
        this.terminalProgressPort = 25001;

        /**
         * Set to true to use 'POS Client' mode (regular VX820 terminal).
         * Set to false to use 'eVo' mode (VX820IP terminal).
         * Set to true when using Engage terminals.
         *
         * @type {boolean}
         */
        this.posClient = true;

        /**
         * An optional user ID to automatically login to the PED.
         * (Both userId and userPin must be supplied to login.)
         *
         * @type {Number}
         */
        this.userId = null;

        /**
         * An optional user PIN to automatically login to the PED.
         * (Both userId and userPin must be supplied to login.)
         *
         * @type {Number}
         */
        this.userPin = null;

        /**
         * The manager PIN is needed when logging in to the terminal if the user is different
         * from the last user that logged in. This should be set along with userId and userPin to make
         * auto login work correctly.
         *
         * @type {Number}
         */
        this.managerPin = null;

        /**
         * It is recommended to turn on 'Raw Printing' in the POS Client application as this results
         * in much better looking receipts.
         * However, with the Aures ODP333 at least, this makes the printer not cut the paper.
         *
         * To solve this, when a 'continue transaction' record is received we can print a small blank
         * document so the paper gets cut.
         *
         * A bonus of this is it automatically continues the transaction too and the operator does not need
         * to press the button.
         *
         * @type {boolean}
         */
        this.improvePrinting = true;

        /**
         * @see {VerifoneIntegrationType}
         * Defaults to POS Client.
         *
         * @type {string}
         */
        this.integrationType = VerifoneIntegrationType.POS_CLIENT;

        /**
         * Additional HTML to print on receipts from Engage terminals.
         *
         * @type {string}
         */
        this.customReceiptHeader = '';

        /**
         * Additional HTML to print on receipts from Engage terminals.
         *
         * @type {string}
         */
        this.customReceiptFooter = '';

        /**
         * List of actions that can be sent in response to a status record.
         * Set to the appropriate list in afterHydration.
         *
         * @type {VerifoneActionList}
         */
        this.actions = new VerifoneActionList();

        /**
         * List of possible status records.
         * Set to the appropriate list in afterHydration.
         *
         * @type {VerifoneStatusList}
         */
        this.statuses = new VerifoneStatusList();

        /**
         * Print extended receipts?
         *
         * @type {boolean}
         */
        this.extendedReceipts = false;
    }

    getHydrationMap() {
        return {
            customReceiptFooter: toString,
            customReceiptHeader: toString,
            improvePrinting: toBool,
            integrationType: (value) => toString(value).toUpperCase(),
            managerPin: toInt,
            posClient: toBool,
            terminalHost: toString,
            terminalPort: toInt,
            terminalProgressPort: toInt,
            userId: toString,
            userPin: toString,
        };
    }

    /**
     * @return {boolean}
     */
    hasLoginCredentials() {
        return this.userId !== null && this.userPin !== null;
    }

    /**
     * @param {string} integrationType
     */
    setIntegrationType(integrationType) {
        // By this point the config has been validated, so trust that everything is as it should be.

        this.integrationType = integrationType;

        switch (this.integrationType) {
            case VerifoneIntegrationType.ENGAGE:
                this.posClient = true;
                this.improvePrinting = false;

                this.actions.setValues(require('./Constants/Engage/Actions')); // eslint-disable-line global-require
                this.statuses.setValues(require('./Constants/Engage/Statuses')); // eslint-disable-line global-require
                break;

            case VerifoneIntegrationType.EVO:
                this.posClient = false;
                this.improvePrinting = false;

                this.actions.setValues({});
                this.statuses.setValues(require('./Constants/Evo/Statuses')); // eslint-disable-line global-require
                break;

            case VerifoneIntegrationType.POS_CLIENT:
                this.posClient = true;
                this.improvePrinting = true;

                if (!this.terminalHost) {
                    // For POS Client we can default the host to localhost.
                    this.terminalHost = '127.0.0.1';
                }

                this.actions.setValues(require('./Constants/PosClient/Actions')); // eslint-disable-line global-require
                this.statuses.setValues(require('./Constants/PosClient/Statuses')); // eslint-disable-line global-require
                break;
        }
    }

    /**
     * Returns true if this is a valid configuration.
     * Otherwise, throws an error.
     *
     * @return {boolean}
     */
    validate() {
        // These text strings do not use translations because configuration is not designed to be done by end users,
        // and it's simpler than translating lang keys.

        if (!this.integrationType) {
            throw new Error('A Terminal Type must be selected.');
        }

        const validIntegrationTypes = [
            VerifoneIntegrationType.ENGAGE,
            VerifoneIntegrationType.EVO,
            VerifoneIntegrationType.POS_CLIENT,
        ];
        if (!validIntegrationTypes.includes(this.integrationType)) {
            throw new Error('The Terminal Type selected is not valid.');
        }

        if (
            this.integrationType !== VerifoneIntegrationType.POS_CLIENT
            && !this.terminalHost
        ) {
            throw new Error('The Terminal IP must be set for this integration type.');
        }

        if (
            this.integrationType !== VerifoneIntegrationType.POS_CLIENT
            && this.terminalHost === '127.0.0.1'
        ) {
            // localhost only makes sense for POS Client integrations.
            throw new Error('The Terminal IP entered is not valid.');
        }

        return true;
    }

    afterHydration() {
        this.setIntegrationType(this.integrationType);
    }
}

module.exports = VerifoneConfig;
